import { Controller } from "@hotwired/stimulus"
import { Turbo } from "@hotwired/turbo-rails"

// Stimulus controller for review action buttons.
// Approve/Clear: one-click via fetch. Reject/Under Review: dialog with required commentary.
// All submissions use fetch + Turbo.renderStreamMessage for reliable turbo stream processing.
export default class extends Controller {
  static values = {
    reviewableType: String,
    reviewableId: Number,
    approveUrl: String,
    rejectUrl: String,
    underReviewUrl: String,
    clearUrl: String,
    noteUrl: String,
    existingNote: { type: String, default: "" },
    // Card #4163 (ADR-0154) -- which Review kind this instance of the primitive
    // acts on ('completion' or 'plan'). Threaded through to the server so
    // ReviewsController#set_kind resolves the right review for every action.
    kind: { type: String, default: "completion" },
    // Mission-only (card #2976 follow-up): Jay wants an approval summary
    // required when approving a Mission, same as Reject/Under Review
    // already require -- Card and Response keep the immediate one-click
    // approve, unchanged. Default false so every existing caller behaves
    // exactly as before; Mission's render call is the only one that opts in.
    requireApprovalCommentary: { type: Boolean, default: false },
    // Card #4751 -- whether the reviewable already has a schedule_start_at,
    // wired straight off the ERB partial (reviewable.schedule_start_at
    // present?) rather than re-deriving it here. Only consulted for the
    // plan kind's Deploy now checkbox default: ON (checked) for an undated
    // mission, OFF for a dated one (ADR-0154 -- MissionStartJob deploys a
    // dated mission automatically on its start date regardless).
    scheduleStartAtPresent: { type: Boolean, default: false }
  }

  static targets = [
    "dialog",
    "dialogForm",
    "dialogHeader",
    "dialogTitle",
    "dialogDescription",
    "commentary",
    "commentaryLabel",
    "commentaryError",
    "confirmButton",
    "deployNowOption",
    "deployNowField",
    "deployNowHelp"
  ]

  connect() {
    this.required = false
    this.pendingUrl = null
    this.pendingMethod = "PATCH"
    this.deployOptionActive = false
  }

  // --- One-click actions ---

  approve(event) {
    if (this.requireApprovalCommentaryValue) {
      const copy = this.approvalCopy()
      const showDeployOption = this.kindValue === "plan"

      this.openDialog({
        url: this.approveUrlValue,
        title: copy.title,
        description: copy.description,
        label: copy.label,
        required: true,
        headerClass: "bg-green-50 border-green-200",
        titleClass: "text-green-900",
        descriptionClass: "text-green-700",
        buttonClass: "bg-green-600 hover:bg-green-700 focus:ring-green-500",
        buttonText: copy.buttonText,
        showDeployOption: showDeployOption,
        // ON by default for an undated mission, OFF for a dated one (card
        // #4751, confirmed both defaults) -- a dated mission already deploys
        // automatically on its start date via MissionStartJob, so leaving
        // the box unchecked there keeps that existing path as the default.
        deployOptionChecked: showDeployOption && !this.scheduleStartAtPresentValue,
        deployOptionHelp: copy.deployOptionHelp
      })
      return
    }

    this.disableButton(event)
    this.submitReview(this.approveUrlValue)
  }

  // Kind-aware copy for the Approve dialog (card #4751, ADR-0154 decision 6).
  // 'plan' reads as a go decision for the mission to proceed, not a sign-off
  // on final field values -- distinct wording from the long-standing
  // completion summary above it. Completion wording is unchanged from before
  // this card.
  approvalCopy() {
    if (this.kindValue === "plan") {
      return {
        title: "Approve plan",
        description: "Record the go decision for this mission's plan. This becomes the plan's official approval and posts to the activity feed.",
        label: "Plan approval comment (required)",
        buttonText: "Approve plan",
        deployOptionHelp: this.scheduleStartAtPresentValue
          ? "Leave unchecked to deploy automatically on the mission's start date."
          : "This mission has no start date, so it will only deploy if you deploy it now."
      }
    }

    return {
      title: "Mission Results Summary",
      description: "Provide your feedback on this mission before approving it as successfully completed. This becomes the mission's official results summary and posts to the activity feed.",
      label: "Mission feedback (required)",
      buttonText: "Approve",
      deployOptionHelp: ""
    }
  }

  clear(event) {
    this.disableButton(event)
    this.submitReview(this.clearUrlValue)
  }

  // --- Dialog-based actions ---

  promptReject() {
    // Plan wording is distinct from the long-standing Response/completion
    // copy (card #4751) -- completion wording is unchanged.
    const isPlan = this.kindValue === "plan"

    this.openDialog({
      url: this.rejectUrlValue,
      title: isPlan ? "Reject plan" : "Reject Response",
      description: isPlan
        ? "Please explain why this mission's plan is being rejected."
        : "Please explain why this response is being rejected.",
      label: "Reason for rejection (required)",
      required: true,
      headerClass: "bg-red-50 border-red-200",
      titleClass: "text-red-900",
      descriptionClass: "text-red-700",
      buttonClass: "bg-red-600 hover:bg-red-700 focus:ring-red-500",
      buttonText: isPlan ? "Reject plan" : "Reject"
    })
  }

  promptUnderReview() {
    const isPlan = this.kindValue === "plan"

    this.openDialog({
      url: this.underReviewUrlValue,
      title: isPlan ? "Mark plan as Under Review" : "Mark as Under Review",
      description: isPlan
        ? "Please add a note about what needs reviewing in this mission's plan."
        : "Please add a note about what needs reviewing.",
      label: "Review notes (required)",
      required: true,
      headerClass: "bg-amber-50 border-amber-200",
      titleClass: "text-amber-900",
      descriptionClass: "text-amber-700",
      buttonClass: "bg-amber-600 hover:bg-amber-700 focus:ring-amber-500",
      buttonText: "Mark Under Review"
    })
  }

  promptNote() {
    this.openDialog({
      url: this.noteUrlValue,
      method: "POST",
      title: "Note",
      description: "Add a note to the activity feed for this response.",
      label: "Note",
      required: true,
      prefill: this.existingNoteValue,
      headerClass: "bg-slate-50 border-slate-200",
      titleClass: "text-slate-900",
      descriptionClass: "text-slate-700",
      buttonClass: "bg-sapphire hover:bg-sapphire/90 focus:ring-sapphire",
      buttonText: "Save Note"
    })
  }

  // --- Dialog management ---

  openDialog({ url, method, title, description, label, required, prefill, headerClass, titleClass, descriptionClass, buttonClass, buttonText, showDeployOption, deployOptionChecked, deployOptionHelp }) {
    if (!this.hasDialogTarget) return

    this.required = required
    this.pendingUrl = url
    this.pendingMethod = method || "PATCH"
    // Card #4751 -- tracked on the instance (not re-derived from the DOM at
    // confirm time) so a Reject/Under Review dialog opened right after a
    // plan Approve dialog can never pick up a stale checked state from the
    // hidden checkbox and send deploy_now on the wrong action.
    this.deployOptionActive = !!showDeployOption

    if (this.hasDeployNowOptionTarget) {
      this.deployNowOptionTarget.classList.toggle("hidden", !showDeployOption)
    }
    if (this.hasDeployNowFieldTarget) {
      this.deployNowFieldTarget.checked = !!deployOptionChecked
    }
    if (this.hasDeployNowHelpTarget) {
      this.deployNowHelpTarget.textContent = deployOptionHelp || ""
    }

    if (this.hasDialogHeaderTarget) {
      this.dialogHeaderTarget.className = `border-b px-6 py-4 ${headerClass}`
    }
    if (this.hasDialogTitleTarget) {
      this.dialogTitleTarget.textContent = title
      this.dialogTitleTarget.className = `text-lg font-semibold ${titleClass}`
    }
    if (this.hasDialogDescriptionTarget) {
      this.dialogDescriptionTarget.textContent = description
      this.dialogDescriptionTarget.className = `mt-1 text-sm ${descriptionClass}`
    }
    if (this.hasCommentaryLabelTarget) {
      this.commentaryLabelTarget.textContent = label
    }
    if (this.hasConfirmButtonTarget) {
      this.confirmButtonTarget.textContent = buttonText
      this.confirmButtonTarget.className = `px-4 py-2 text-sm font-medium text-white rounded-md cursor-pointer ${buttonClass}`
    }

    // Pre-populate or reset
    if (this.hasCommentaryTarget) {
      this.commentaryTarget.value = prefill || ""
    }
    if (this.hasCommentaryErrorTarget) {
      this.commentaryErrorTarget.classList.add("hidden")
    }

    this.dialogTarget.showModal()

    requestAnimationFrame(() => {
      if (this.hasCommentaryTarget) {
        this.commentaryTarget.focus()
      }
    })
  }

  closeDialog() {
    if (this.hasDialogTarget) {
      this.dialogTarget.close()
    }
    this.pendingUrl = null
    this.pendingMethod = "PATCH"
    this.deployOptionActive = false
  }

  confirmAction(event) {
    event.preventDefault()

    const commentary = this.hasCommentaryTarget ? this.commentaryTarget.value.trim() : ""

    if (this.required && this.plainCommentaryLength() < 5) {
      if (this.hasCommentaryErrorTarget) {
        this.commentaryErrorTarget.classList.remove("hidden")
      }
      if (this.hasCommentaryTarget) {
        this.commentaryTarget.focus()
      }
      return
    }

    if (!this.pendingUrl) return

    const url = this.pendingUrl
    const method = this.pendingMethod
    const isNote = url === this.noteUrlValue
    // Only read the checkbox while THIS dialog's deploy option is actually
    // the one showing (card #4751) -- guards against a leftover checked
    // state being sent on an unrelated action.
    const deployNow = this.deployOptionActive && this.hasDeployNowFieldTarget
      ? this.deployNowFieldTarget.checked
      : false
    this.closeDialog()
    this.submitReview(url, commentary, method, deployNow)

    // Remember the commentary so the next Note click pre-populates
    if (isNote) {
      this.existingNoteValue = commentary
    }
  }

  // Commentary is now a Trix editor (card #2867 -- @mention support), so
  // .value returns serialised HTML, not plain text. A short comment could
  // pad past the 5-char minimum with markup/attachment tags alone, so the
  // length check measures the editor's visible text instead. .value (HTML +
  // attachments) is still what actually gets submitted in submitReview.
  plainCommentaryLength() {
    if (!this.hasCommentaryTarget) return 0
    const editor = this.commentaryTarget.editor
    if (editor) return editor.getDocument().toString().trim().length
    // Editor not yet initialized (rare timing edge) -- strip markup so a short
    // comment can't pad past the minimum via HTML tags alone.
    return this.commentaryTarget.value.replace(/<[^>]*>/g, "").trim().length
  }

  // --- Shared fetch submission ---

  async submitReview(url, commentary = "", method = "PATCH", deployNow = false) {
    const csrfToken = document.querySelector('meta[name="csrf-token"]')?.content
    const params = new URLSearchParams({
      reviewable_type: this.reviewableTypeValue,
      reviewable_id: this.reviewableIdValue,
      kind: this.kindValue
    })
    if (commentary) {
      params.append("review_commentary", commentary)
    }
    if (deployNow) {
      params.append("deploy_now", "true")
    }

    try {
      const response = await fetch(url, {
        method: method,
        headers: {
          "Content-Type": "application/x-www-form-urlencoded",
          "Accept": "text/vnd.turbo-stream.html",
          "X-CSRF-Token": csrfToken
        },
        body: params
      })

      if (response.ok) {
        const html = await response.text()
        Turbo.renderStreamMessage(html)
        // Notify any interested controller (e.g. the gallery main-image chip) that
        // a review landed. The image context menu lives OUTSIDE the gallery's DOM
        // subtree, so a document-level event is the reliable cross-controller path
        // — the gallery chip has no per-image turbo target to replace. We carry
        // the RESULTING STATUS in the detail so listeners don't have to read it
        // back from the DOM: the modal_review_indicator dot can be duplicated
        // across dual-pane previews, so only one of the duplicates gets replaced
        // by the stream — reading it back would be unreliable. (#1808)
        document.dispatchEvent(new CustomEvent("review:updated", {
          detail: {
            reviewableType: this.reviewableTypeValue,
            reviewableId: this.reviewableIdValue,
            status: this.statusForUrl(url)
          }
        }))
      }
    } catch (error) {
      // Allow the page to remain usable on network errors
    }
  }

  // --- Helpers ---

  // Map the submitted endpoint to the resulting review status (the Review enum
  // value). Matched to the *Url values wired on the controller element. Clear
  // returns "" (unreviewed). Note posts don't change status — they never reach
  // submitReview's status path with a review URL, so default to "".
  statusForUrl(url) {
    switch (url) {
      case this.approveUrlValue: return "approved"
      case this.rejectUrlValue: return "rejected"
      case this.underReviewUrlValue: return "deciding"
      case this.clearUrlValue: return ""
      default: return ""
    }
  }

  disableButton(event) {
    const button = event.target.closest("button")
    if (button) {
      button.disabled = true
      button.classList.add("opacity-50", "cursor-not-allowed")
    }
  }
}
